import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Award, Download, ArrowLeft, CheckCircle2 } from "lucide-react";

const CertificatePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    checkAndGenerateCertificate();
  }, []);

  const checkAndGenerateCertificate = async () => {
    try {
      const response = await axios.post(`${API}/certificate/generate`);
      setCertificate(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Vous devez compléter tous les modules pour obtenir le certificat");
        navigate("/dashboard");
      } else {
        toast.error("Erreur lors de la génération du certificat");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(`${API}/certificate/download`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `certificat-${user.full_name.replace(" ", "-")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Certificat téléchargé avec succès!");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-cream-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Message */}
        <div className="text-center mb-8 animate-fadeIn">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-100 to-purple-100 rounded-full mb-4">
            <Award className="w-10 h-10 text-orange-600" />
          </div>
          <h1 className="text-4xl font-bold gradient-text mb-2">
            Félicitations!
          </h1>
          <p className="text-lg text-gray-600">
            Vous avez complété la formation d'animateur parascolaire
          </p>
        </div>

        {/* Certificate Preview Card */}
        <Card className="border-0 shadow-2xl animate-fadeIn" style={{ animationDelay: "0.2s" }}>
          <CardHeader className="bg-gradient-to-r from-orange-500 to-purple-600 text-white">
            <CardTitle className="text-center text-2xl">
              Certificat de Formation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="text-center space-y-6">
              <div>
                <p className="text-gray-600 mb-2">Ce certificat est décerné à</p>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-testid="certificate-user-name">
                  {certificate?.user_name}
                </h2>
              </div>

              <div className="space-y-2">
                <p className="text-gray-700">
                  Pour avoir complété avec succès la formation
                </p>
                <p className="text-xl font-semibold text-orange-600">
                  Animateur Parascolaire Les A.C.E.S.
                </p>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Formation validée</span>
                </div>
                <p className="text-sm text-gray-500">
                  N° de certificat: <span className="font-mono font-semibold" data-testid="certificate-number">{certificate?.certificate_number}</span>
                </p>
                <p className="text-sm text-gray-500">
                  Délivré le: {new Date(certificate?.issued_at).toLocaleDateString("fr-FR")}
                </p>
              </div>

              <div className="pt-6">
                <Button
                  onClick={downloadCertificate}
                  disabled={generating}
                  className="btn-primary text-white px-8 py-6 text-lg"
                  data-testid="download-certificate-button"
                >
                  <Download className="w-5 h-5 mr-2" />
                  {generating ? "Téléchargement..." : "Télécharger le certificat PDF"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mt-8 border-0 shadow-md animate-fadeIn" style={{ animationDelay: "0.4s" }}>
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Étapes suivantes</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                <span>Téléchargez votre certificat PDF pour vos dossiers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                <span>Partagez votre réussite avec votre coordinateur</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                <span>Commencez vos animations avec confiance et professionnalisme</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CertificatePage;
